"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Savings/value section — 2026-08-29 interactive redesign (owner ask:
 * "fluid, like the latest AI UIs"):
 *
 *  - The big euro number is now a LIVE readout: selecting a habit card (or
 *    a bar segment) morphs the number to that habit's amount with a
 *    spring slide; deselecting returns to the total. One number, one
 *    scale, zero mystery.
 *  - The proportional stacked bar animates its segments open on reveal
 *    and dims the non-active ones on hover/selection (two-way with the
 *    cards).
 *  - The methodology paragraph (owner: "bulky, not relevant there") moved
 *    into a collapsible "How is this calculated?" disclosure — the
 *    truthfulness stays one tap away instead of shouting at every
 *    visitor.
 *
 * All amounts remain the clearly-labeled illustrative sample; the offers
 * row stays an honest "coming soon" with no amount and no bar segment.
 */

import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

// Parity correction 2026-09-10: only the two savings kinds the product can
// actually attribute appear with a euro figure. `services/planning` emits
// `pantry_reuse` and `offer` and nothing else, and `InsightsResponse.byKind`
// carries only what it emits — so "batch cooking" and "lower-cost meals" can
// never be priced and are no longer presented as if they were.
// Precomputed shares of the €12.40 total (8.50/3.90 → 69/31, sums to 100).
// Recompute by hand if the sample breakdown amounts ever change — see
// landing.en.ts/landing.de.ts's savings.breakdown* keys.
const SEGMENTS = [
  { key: "pantry", percent: 69, color: "bg-lime" },
  { key: "offer", percent: 31, color: "bg-sky" },
] as const

export function SavingsSection() {
  const { t } = useI18n()
  const reduceMotion = useReducedMotion()
  const [hovered, setHovered] = useState<number | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const active = hovered ?? selected

  const rows = [
    {
      label: t("landingPage.savings.breakdownPantryLabel"),
      amount: t("landingPage.savings.breakdownPantryAmount"),
      caption: t("landingPage.savings.breakdownPantryCaption"),
      tag: null as string | null,
    },
    {
      label: t("landingPage.savings.breakdownOffersLabel"),
      amount: t("landingPage.savings.breakdownOffersAmount"),
      caption: t("landingPage.savings.breakdownOffersCaption"),
      tag: t("landingPage.savings.breakdownOffersTag"),
    },
  ]

  const shownAmount =
    selected === null ? t("landingPage.savings.exampleAmount") : rows[selected].amount
  const shownLabel = selected === null ? t("landingPage.savings.totalLabel") : rows[selected].label

  return (
    <section id="savings" className="scroll-mt-[12vh] border-b border-line bg-ink text-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-lime uppercase">
            {t("landingPage.savings.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl">
            {t("landingPage.savings.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-paper/80">{t("landingPage.savings.lead")}</p>
        </Reveal>

        <Reveal
          index={1}
          className="mt-10 rounded-bb-xl border border-paper/15 bg-paper/5 p-6 sm:p-8"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="text-xs font-semibold tracking-wide text-paper/70 uppercase">
                {t("landingPage.savings.exampleLabel")}
              </span>
              {/* Live readout: morphs between the total and the selected
                  habit's amount. aria-live announces the change. */}
              <div aria-live="polite" className="relative min-h-[3.5rem] overflow-hidden">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.p
                    key={shownAmount + shownLabel}
                    initial={reduceMotion ? false : { y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={reduceMotion ? undefined : { y: -24, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    className="text-display font-black text-lime"
                  >
                    {shownAmount}
                    <span className="ml-3 align-middle text-sm font-semibold text-paper/70">
                      {shownLabel}
                    </span>
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
            <p className="max-w-xs text-right text-xs text-paper/60">
              {t("landingPage.savings.methodNote")}
            </p>
          </div>

          <div
            role="img"
            aria-label={`${t("landingPage.savings.barAria")}. ${rows
              .map(r => `${r.label}: ${r.amount}`)
              .join(", ")}`}
            className="mt-6 flex h-3.5 w-full gap-0.5 overflow-hidden rounded-bb-full bg-paper/10"
          >
            {SEGMENTS.map((segment, i) => (
              <motion.button
                key={segment.key}
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setSelected(prev => (prev === i ? null : i))}
                initial={reduceMotion ? false : { width: 0 }}
                whileInView={{ width: `${segment.percent}%` }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  stiffness: 120,
                  damping: 22,
                  delay: 0.15 + i * 0.12,
                }}
                className={`h-full cursor-pointer ${segment.color} transition-opacity duration-300`}
                style={{ opacity: active === null || active === i ? 1 : 0.3 }}
              />
            ))}
          </div>

          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {rows.map((row, i) => {
              const isSelected = selected === i
              return (
                <li key={row.label}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(i)}
                    onBlur={() => setHovered(null)}
                    onClick={() => setSelected(prev => (prev === i ? null : i))}
                    className={`w-full rounded-bb-lg border p-4 text-left transition-all duration-300 focus:outline-none focus-visible:border-lime/60 ${
                      isSelected
                        ? "border-lime/70 bg-paper/10 shadow-[0_0_24px_rgba(214,232,90,0.08)]"
                        : "border-paper/10 bg-paper/[0.03] hover:border-paper/25"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <span
                          aria-hidden="true"
                          className={`size-2.5 rounded-full ${SEGMENTS[i].color} transition-transform duration-300 ${
                            isSelected ? "scale-125" : ""
                          }`}
                        />
                        {row.label}
                      </span>
                      <span className="flex items-center gap-2">
                        {row.tag && (
                          <span className="rounded-bb-full bg-lime px-2 py-0.5 text-[10px] font-bold tracking-wide text-ink uppercase">
                            {row.tag}
                          </span>
                        )}
                        <strong className="text-sm">{row.amount}</strong>
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-paper/65">{row.caption}</p>
                  </button>
                </li>
              )
            })}
            {/* Batch cooking: a real habit the planner builds in, but one the
                savings calculator never prices. It keeps its row and its story
                and carries no euro figure — the same honest slot the offers row
                held while offers were still dark. */}
            <li className="rounded-bb-lg border border-dashed border-paper/20 bg-transparent p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium text-paper/70">
                  <span
                    aria-hidden="true"
                    className="size-2.5 rounded-full border border-paper/40"
                  />
                  {t("landingPage.savings.breakdownBatchLabel")}
                </span>
                <span className="text-xs text-paper/55">
                  {t("landingPage.savings.breakdownBatchNoAmount")}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-paper/50">
                {t("landingPage.savings.breakdownBatchCaption")}
              </p>
            </li>
          </ul>

          {/* Methodology, one tap away (owner: the always-on paragraph was
              bulky) — the full calculation basis is unchanged inside. */}
          <details className="group mt-6 border-t border-paper/15 pt-4">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-paper/70 transition-colors hover:text-paper [&::-webkit-details-marker]:hidden">
              <ChevronDown
                className="size-4 transition-transform duration-300 group-open:rotate-180"
                aria-hidden="true"
              />
              {t("landingPage.savings.howCalculated")}
            </summary>
            <p className="mt-3 max-w-prose text-sm text-paper/70">
              {t("landingPage.savings.calculationBasis")}
            </p>
          </details>
        </Reveal>
      </div>
    </section>
  )
}
